import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

export interface LiveImageResponse {
  name: string;
  signedUrl: string;
  photoSavedDate: Date;
}

export interface ImageRequestResponse {
  serialNumber: string;
  requestId: string;
  views: string[];
  status: string;
  requestedAt?: string;
  reason?: string;
}

export interface LiveImageResult {
  status: string;
  images: LiveImageResponse[];
}

// Internal (bearerless) Live Image API — the api mints its own token server-side, so no auth header.
@Injectable({
  providedIn: 'root',
})
export class LiveImageService {
  private readonly url = environment.liveImage;
  private static readonly POLL_INTERVAL_MS = 5000;
  private static readonly MAX_POLLS = 60;
  private static readonly STATUS_COMPLETE = 'COMPLETE';

  constructor(private httpClient: HttpClient) {}

  // Force a fresh capture of the given views, polling requeststatus until images arrive or a terminal status.
  async requestNewImages(serialNumber: string, views: string[], isLytxOneHub: boolean): Promise<LiveImageResult> {
    const params = new HttpParams()
      .set('serialNumber', serialNumber)
      .set('isLytxOneHub', isLytxOneHub);

    const initial = await this.httpClient
      .post<LiveImageResponse[] | ImageRequestResponse>(
        `${this.url}/internal/liveimage`,
        { DataTypes: views },
        { params, observe: 'response' },
      )
      .toPromise();

    return this.pollToCompletion(serialNumber, views, initial);
  }

  // Load images: cached when present, or trigger a new capture when none and isRequestNewImage is set.
  async getLiveImages(serialNumber: string, views: string[], isRequestNewImage: boolean, isLytxOneHub: boolean): Promise<LiveImageResult> {
    const params = new HttpParams()
      .set('isRequestNewImage', isRequestNewImage)
      .set('isLytxOneHub', isLytxOneHub);

    const initial = await this.httpClient
      .post<LiveImageResponse[] | ImageRequestResponse>(
        `${this.url}/internal/liveimage/${serialNumber}`,
        views,
        { params, observe: 'response' },
      )
      .toPromise();

    return this.pollToCompletion(serialNumber, views, initial);
  }

  private async pollToCompletion(
    serialNumber: string,
    views: string[],
    initial: HttpResponse<LiveImageResponse[] | ImageRequestResponse>,
  ): Promise<LiveImageResult> {
    let current = initial;
    for (let attempt = 0; attempt < LiveImageService.MAX_POLLS; attempt++) {
      // 200 + array = images ready; 200 + object = terminal non-complete status (e.g. DEVICE_NOT_COMMUNICATING).
      if (current.status === 200 && Array.isArray(current.body)) {
        return { status: LiveImageService.STATUS_COMPLETE, images: current.body };
      }
      const pending = current.body as ImageRequestResponse;
      if (current.status === 200 || !pending?.requestId) {
        return { status: pending?.status ?? 'UNKNOWN', images: [] };
      }
      await this.delay(LiveImageService.POLL_INTERVAL_MS);
      current = await this.getRequestStatus(
        serialNumber,
        pending.requestId,
        views,
        pending.requestedAt ?? new Date().toISOString(),
      );
    }
    return { status: 'TIMEOUT', images: [] };
  }

  private getRequestStatus(
    serialNumber: string,
    requestId: string,
    views: string[],
    requestedAt: string,
  ): Promise<HttpResponse<LiveImageResponse[] | ImageRequestResponse>> {
    const params = new HttpParams().set('requestedAt', requestedAt);

    return this.httpClient
      .post<LiveImageResponse[] | ImageRequestResponse>(
        `${this.url}/internal/liveimage/${serialNumber}/requeststatus/${requestId}`,
        views,
        { params, observe: 'response' },
      )
      .toPromise();
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
